import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { isTestAccount } from "@/lib/testAccounts";
import { format, parseISO } from "date-fns";
import {
  CalendarClock,
  Instagram,
  Sparkles,
  Check,
  X,
  Loader2,
  ExternalLink,
  Bell,
} from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange?: () => void;
}

interface RescheduleRow {
  id: string;
  service_name: string;
  service_category: string;
  booking_date: string;
  booking_time: string;
  duration: string;
  price: number;
  user_id: string;
  reschedule_requested_at: string | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
}

interface AddonRow {
  id: string;
  name: string;
  price: number;
  duration: string;
  booking_id: string;
  user_id: string;
  created_at: string;
  service_name: string | null;
  booking_date: string | null;
  booking_time: string | null;
  client_name: string | null;
}

interface IgRow {
  id: string;
  post_url: string;
  user_id: string;
  created_at: string;
  client_name: string | null;
  client_email: string | null;
}

const ActionNeededDialog = ({ open, onOpenChange, onChange }: Props) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [reschedules, setReschedules] = useState<RescheduleRow[]>([]);
  const [addons, setAddons] = useState<AddonRow[]>([]);
  const [igPosts, setIgPosts] = useState<IgRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);

    // Read banner filter settings (mirror Dashboard banner counts)
    const { data: settingsRows } = await supabase
      .from("site_settings")
      .select("key, value")
      .in("key", ["banner_exclude_test_accounts", "banner_exclude_inactive_bookings"]);
    const settingsMap = new Map((settingsRows || []).map((r: any) => [r.key, r.value]));
    const excludeTest = settingsMap.get("banner_exclude_test_accounts") !== "false";
    const excludeInactive = settingsMap.get("banner_exclude_inactive_bookings") !== "false";

    const todayStr = new Date().toISOString().split("T")[0];
    const isInactiveBooking = (status?: string | null, dateStr?: string | null) =>
      status === "cancelled" ||
      status === "completed" ||
      (!!dateStr && dateStr < todayStr);

    const [reschedRes, addonRes, igRes] = await Promise.all([
      supabase
        .from("bookings")
        .select("*").is("deleted_at", null)
        .eq("status", "pending")
        .not("reschedule_requested_at", "is", null)
        .order("reschedule_requested_at", { ascending: false }),
      supabase
        .from("booking_addons")
        .select("id, name, price, duration, booking_id, user_id, created_at")
        .eq("status", "pending")
        .eq("requested_by", "client")
        .order("created_at", { ascending: false }),
      supabase
        .from("instagram_posts")
        .select("id, post_url, user_id, created_at")
        .eq("status", "pending")
        .order("created_at", { ascending: false }),
    ]);

    // Build profile map for everyone we need
    const allUserIds = Array.from(
      new Set([
        ...((reschedRes.data || []) as any[]).map((r) => r.user_id),
        ...((addonRes.data || []) as any[]).map((r) => r.user_id),
        ...((igRes.data || []) as any[]).map((r) => r.user_id),
      ]),
    );
    let profileMap = new Map<string, { full_name: string | null; email: string | null; phone: string | null }>();
    if (allUserIds.length > 0) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, full_name, email, phone")
        .in("user_id", allUserIds);
      profileMap = new Map(
        (profs || []).map((p: any) => [
          p.user_id,
          { full_name: p.full_name, email: p.email, phone: p.phone },
        ]),
      );
    }

    // Resolve parent bookings for add-on requests so we can filter inactive ones
    const addonRows = (addonRes.data || []) as any[];
    const parentBookingIds = Array.from(new Set(addonRows.map((a) => a.booking_id).filter(Boolean)));
    let parentMap = new Map<
      string,
      { status: string; booking_date: string; service_name: string; booking_time: string }
    >();
    if (parentBookingIds.length > 0) {
      const { data: parents } = await supabase
        .from("bookings")
        .select("id, status, booking_date, service_name, booking_time").is("deleted_at", null)
        .in("id", parentBookingIds);
      parentMap = new Map(
        (parents || []).map((p: any) => [
          p.id,
          {
            status: p.status,
            booking_date: p.booking_date,
            service_name: p.service_name,
            booking_time: p.booking_time,
          },
        ]),
      );
    }

    const filterTest = (rows: any[]) =>
      excludeTest ? rows.filter((r: any) => !isTestAccount(r.user_id)) : rows;

    const reschedFiltered = filterTest((reschedRes.data || []) as any[])
      .filter((b: any) => (excludeInactive ? !isInactiveBooking(b.status, b.booking_date) : true))
      .map((b: any) => ({
        ...b,
        client_name: profileMap.get(b.user_id)?.full_name ?? null,
        client_email: profileMap.get(b.user_id)?.email ?? null,
        client_phone: profileMap.get(b.user_id)?.phone ?? null,
      })) as RescheduleRow[];

    const addonFiltered = filterTest(addonRows)
      .filter((a: any) => {
        if (!excludeInactive) return true;
        const parent = parentMap.get(a.booking_id);
        return parent ? !isInactiveBooking(parent.status, parent.booking_date) : true;
      })
      .map((a: any) => {
        const parent = parentMap.get(a.booking_id);
        return {
          ...a,
          service_name: parent?.service_name ?? null,
          booking_date: parent?.booking_date ?? null,
          booking_time: parent?.booking_time ?? null,
          client_name: profileMap.get(a.user_id)?.full_name ?? null,
        };
      }) as AddonRow[];

    const igFiltered = filterTest((igRes.data || []) as any[]).map((p: any) => ({
      ...p,
      client_name: profileMap.get(p.user_id)?.full_name ?? null,
      client_email: profileMap.get(p.user_id)?.email ?? null,
    })) as IgRow[];

    setReschedules(reschedFiltered);
    setAddons(addonFiltered);
    setIgPosts(igFiltered);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  // -------- Reschedule actions --------
  const approveReschedule = async (b: RescheduleRow) => {
    setBusy(`r-${b.id}`);
    const { error } = await supabase
      .from("bookings")
      .update({ status: "confirmed", reschedule_requested_at: null })
      .eq("id", b.id);
    if (error) {
      toast({ title: "Failed to approve", description: error.message, variant: "destructive" });
      setBusy(null);
      return;
    }
    // Notify client (best-effort)
    try {
      if (b.client_phone) {
        supabase.functions.invoke("send-booking-sms", {
          body: {
            type: "confirmation",
            phone: b.client_phone,
            service_name: b.service_name,
            booking_date: b.booking_date,
            booking_time: b.booking_time,
            duration: b.duration,
            price: b.price,
          },
        }).catch(() => {});
      }
      if (b.client_email) {
        supabase.functions.invoke("send-booking-email", {
          body: {
            type: "confirmation",
            email: b.client_email,
            service_name: b.service_name,
            service_category: b.service_category,
            booking_date: b.booking_date,
            booking_time: b.booking_time,
            duration: b.duration,
            price: b.price,
          },
        }).catch(() => {});
      }
    } catch {}
    toast({ title: "Reschedule approved" });
    setBusy(null);
    setReschedules((prev) => prev.filter((r) => r.id !== b.id));
    onChange?.();
  };

  const declineReschedule = async (b: RescheduleRow) => {
    setBusy(`r-${b.id}`);
    const { error } = await supabase
      .from("bookings")
      .update({ status: "cancelled" })
      .eq("id", b.id);
    setBusy(null);
    if (error) {
      toast({ title: "Failed to decline", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Reschedule declined" });
    setReschedules((prev) => prev.filter((r) => r.id !== b.id));
    onChange?.();
  };

  // -------- Add-on actions --------
  const setAddonStatus = async (a: AddonRow, next: "confirmed" | "declined") => {
    setBusy(`a-${a.id}`);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("booking_addons")
      .update({
        status: next,
        confirmed_by: user?.id ?? null,
        confirmed_at: new Date().toISOString(),
      })
      .eq("id", a.id);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      setBusy(null);
      return;
    }
    try {
      await supabase.functions.invoke("notify-addon-request", {
        body: { booking_id: a.booking_id, addon_ids: [a.id], event: next },
      });
    } catch (e) {
      console.error("notify failed", e);
    }
    toast({ title: next === "confirmed" ? "Add-on approved" : "Add-on declined" });
    setBusy(null);
    setAddons((prev) => prev.filter((r) => r.id !== a.id));
    onChange?.();
  };

  // -------- IG actions --------
  const approveIg = async (post: IgRow) => {
    setBusy(`i-${post.id}`);
    const { error } = await supabase
      .from("instagram_posts")
      .update({ status: "approved" })
      .eq("id", post.id);
    if (error) {
      toast({ title: "Failed to approve", description: error.message, variant: "destructive" });
      setBusy(null);
      return;
    }
    // Issue $5 credit if not already issued for this post
    const { data: existingCoupon } = await supabase
      .from("coupons")
      .select("id")
      .eq("user_id", post.user_id)
      .eq("reason", "instagram_share")
      .eq("booking_id", post.id)
      .maybeSingle();

    if (!existingCoupon) {
      const code = "IG-" + Math.random().toString(36).substring(2, 8).toUpperCase();
      const igExpiry = new Date();
      igExpiry.setMonth(igExpiry.getMonth() + 6);
      await supabase.from("coupons").insert({
        user_id: post.user_id,
        code,
        discount_amount: 5,
        reason: "instagram_share",
        booking_id: null,
        expires_at: igExpiry.toISOString(),
      });
      if (post.client_email) {
        supabase.functions
          .invoke("send-credit-email", {
            body: {
              email: post.client_email,
              client_name: post.client_name,
              coupon_code: code,
              discount_amount: 5,
              expires_at: igExpiry.toISOString(),
              reason: "instagram_share",
            },
          })
          .catch(() => {});
      }
    }
    toast({
      title: "IG post approved",
      description: `$5 credit issued to ${post.client_name || "client"}.`,
    });
    setBusy(null);
    setIgPosts((prev) => prev.filter((p) => p.id !== post.id));
    onChange?.();
  };

  const rejectIg = async (post: IgRow) => {
    setBusy(`i-${post.id}`);
    const { error } = await supabase
      .from("instagram_posts")
      .update({ status: "rejected" })
      .eq("id", post.id);
    setBusy(null);
    if (error) {
      toast({ title: "Failed to reject", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "IG post rejected" });
    setIgPosts((prev) => prev.filter((p) => p.id !== post.id));
    onChange?.();
  };

  const total = reschedules.length + addons.length + igPosts.length;

  const formatBookingDate = (date?: string | null, time?: string | null) => {
    if (!date) return "";
    try {
      return `${format(parseISO(date), "MMM d, yyyy")}${time ? ` · ${time}` : ""}`;
    } catch {
      return date;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif italic">
            <Bell size={18} className="text-primary" /> Action needed
          </DialogTitle>
          <DialogDescription>
            {loading
              ? "Loading pending items…"
              : total === 0
                ? "Nothing waiting for your review. 🎉"
                : `${total} item${total === 1 ? "" : "s"} waiting for your review.`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 size={18} className="animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Reschedule requests */}
            {reschedules.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-medium tracking-[0.2em] uppercase text-primary flex items-center gap-2">
                    <CalendarClock size={13} /> Reschedule requests
                  </h3>
                  <Badge variant="outline" className="text-[10px] tracking-widest uppercase">
                    {reschedules.length}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {reschedules.map((b) => (
                    <div
                      key={b.id}
                      className="flex items-start justify-between gap-3 p-3 border border-border/50 bg-card"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {b.client_name || "Unknown client"}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {b.service_name} · {formatBookingDate(b.booking_date, b.booking_time)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10"
                          disabled={busy === `r-${b.id}`}
                          onClick={() => approveReschedule(b)}
                          title="Approve"
                        >
                          {busy === `r-${b.id}` ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-500/10"
                          disabled={busy === `r-${b.id}`}
                          onClick={() => declineReschedule(b)}
                          title="Decline"
                        >
                          <X size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-muted-foreground"
                          onClick={() => {
                            onOpenChange(false);
                            navigate("/admin/bookings?filter=pending");
                          }}
                          title="Open in Bookings"
                        >
                          <ExternalLink size={13} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {reschedules.length > 0 && (addons.length > 0 || igPosts.length > 0) && <Separator />}

            {/* Add-on requests */}
            {addons.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-medium tracking-[0.2em] uppercase text-primary flex items-center gap-2">
                    <Sparkles size={13} /> Add-on requests
                  </h3>
                  <Badge variant="outline" className="text-[10px] tracking-widest uppercase">
                    {addons.length}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {addons.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-start justify-between gap-3 p-3 border border-border/50 bg-card"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{a.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {a.client_name || "Client"} ·{" "}
                          {a.service_name ? `${a.service_name} · ` : ""}
                          {formatBookingDate(a.booking_date, a.booking_time)} · +$
                          {Number(a.price).toFixed(2)} · {a.duration}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10"
                          disabled={busy === `a-${a.id}`}
                          onClick={() => setAddonStatus(a, "confirmed")}
                          title="Approve"
                        >
                          {busy === `a-${a.id}` ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-500/10"
                          disabled={busy === `a-${a.id}`}
                          onClick={() => setAddonStatus(a, "declined")}
                          title="Decline"
                        >
                          <X size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {addons.length > 0 && igPosts.length > 0 && <Separator />}

            {/* IG credit approvals */}
            {igPosts.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-medium tracking-[0.2em] uppercase text-primary flex items-center gap-2">
                    <Instagram size={13} /> IG credit approvals
                  </h3>
                  <Badge variant="outline" className="text-[10px] tracking-widest uppercase">
                    {igPosts.length}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {igPosts.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-start justify-between gap-3 p-3 border border-border/50 bg-card"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">
                          {p.client_name || "Client"}
                        </p>
                        <a
                          href={p.post_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-primary underline truncate block"
                        >
                          {p.post_url}
                        </a>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10"
                          disabled={busy === `i-${p.id}`}
                          onClick={() => approveIg(p)}
                          title="Approve & issue $5 credit"
                        >
                          {busy === `i-${p.id}` ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-rose-600 hover:text-rose-700 hover:bg-rose-500/10"
                          disabled={busy === `i-${p.id}`}
                          onClick={() => rejectIg(p)}
                          title="Reject"
                        >
                          <X size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-muted-foreground"
                          onClick={() => {
                            onOpenChange(false);
                            navigate("/admin/instagram-approvals");
                          }}
                          title="Open in IG Approvals"
                        >
                          <ExternalLink size={13} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {total === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                All caught up. Nothing to review right now.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ActionNeededDialog;
